import { useEffect, useRef } from "react";
import { useEcomStore } from "@/context/ecom/EcomContext";
import getConfigTID from "@/lib/getConfigTID";
import { useLogin } from "@/context/login/LoginContext";
import { isUserAuthenticated } from "@/context/login/reducer";
import { getLegacyTabHash } from "@/lib/legacyTabQueryToHash";
import { scheduleScrollToAnchor } from "@/lib/scrollToAnchor";
import { isKnownUser } from "@/lib/knownUserCookie";
const HASH_STORAGE_KEY = "mxp_silent_sso_hash";
const UTM_STORAGE_KEY = "mxp_silent_sso_utm_params";
const REFERRER_STORAGE_KEY = "mxp_silent_sso_referrer";
const UTM_PARAMS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content", "utm_partner", "utm_impartnerId", "search", "searchFilter", "searchIndex"];

/**
 * OAuth-related query parameters that indicate an active or completed OAuth
 * redirect flow.  Their presence means we should NOT fire silent SSO.
 */
const OAUTH_FLOW_PARAMS = ["code", "state", "identity_provider"];

/**
 * Set right before firing the `prompt=none` redirect, cleared once we land
 * back and process the result. Survives the round trip through TID's domain
 * (and the mxp-api redirect_uri relay) since sessionStorage is per-origin,
 * not per-navigation. Used to detect "we just tried this and it didn't log
 * us in" so a failure that comes back without any distinguishing query
 * param (see loop-breaker below) can't cause an infinite redirect loop.
 */
export const SSO_REDIRECT_FLAG = "mxp_sso_redirect";

/**
 * Silent SSO: if not authenticated and no OAuth params, redirect to TID with prompt=none.
 *
 * Only one caller of this hook should ever be "live" (i.e. passing
 * `disableLogin: false`) on a given page. `isAuthCheckComplete` lives in the
 * app-wide EcomContext, so two simultaneously-live callers would race to
 * redirect over the same shared flag. LayoutComponent's call is the single
 * source of truth for every route (see its `disableLogin` computation, which
 * excludes Trimble Developer routes from the sitewide nav's CMS-configured
 * `disableLogin` field so TD still gets real silent-SSO login persistence).
 */
const useAuthCheck = ({
  disableLogin
}: {
  disableLogin?: boolean;
} = {}) => {
  const [state, dispatch] = useEcomStore() ?? [];
  const loginCtx = useLogin();
  const [loginState] = loginCtx ?? [];
  const oauthCleanupDoneRef = useRef(false);

  // Note: re-cleaning OAuth params that resurface on Back/Forward is
  // handled separately by <SsoParamsCleaner />, which reacts to Next's
  // router state (useSearchParams) instead of guessing with a popstate
  // + timeout. See that component for why.

  useEffect(() => {
    // 1. Is auth check disabled?
    if (process.env.NEXT_PUBLIC_BYPASS_LOGIN === "true" || disableLogin) {
      if (!state?.isAuthCheckComplete) {
        dispatch?.({
          type: "SET_AUTH_CHECK_COMPLETE",
          payload: true
        });
      }
      return;
    }
    const searchParams = new URLSearchParams(window.location.search);

    // 2. After auth check is complete, clean up OAuth params
    if (state?.isAuthCheckComplete) {
      if (oauthCleanupDoneRef.current) return;
      oauthCleanupDoneRef.current = true;
      searchParams.delete("code");
      searchParams.delete("state");
      searchParams.delete("error");
      searchParams.delete("error_description");
      searchParams.delete("identity_provider");
      const storedUtmParams = sessionStorage.getItem(UTM_STORAGE_KEY);
      if (storedUtmParams) {
        try {
          const parsedUtmParams = JSON.parse(storedUtmParams) as Record<string, string>;
          for (const key of UTM_PARAMS) {
            if (!searchParams.has(key) && parsedUtmParams[key]) {
              searchParams.set(key, parsedUtmParams[key]);
            }
          }
        } catch {
          // Ignore parse errors and continue with OAuth cleanup only.
        }
        sessionStorage.removeItem(UTM_STORAGE_KEY);
      }
      const storedHash = sessionStorage.getItem(HASH_STORAGE_KEY) ?? window.location.hash;
      sessionStorage.removeItem(HASH_STORAGE_KEY);
      const newUrl = searchParams.size > 0 ? `${window.location.pathname}?${searchParams.toString()}` : window.location.pathname;
      // Not router.replace: its full-reload fallback drops the in-memory login and restarts silent SSO.
      window.history.replaceState(null, "", newUrl + storedHash);
      if (storedHash) {
        scheduleScrollToAnchor(storedHash);
      }
      return;
    }

    // 3. If there's an OAuth error (e.g., after logout), mark auth check complete to prevent redirect loop
    const hasError = searchParams.has("error");
    const hasNullCode = searchParams.get("code") === "null";
    const hasOrphanedState = searchParams.has("state") && !searchParams.has("code");
    if (hasError || hasNullCode || hasOrphanedState) {
      sessionStorage.removeItem(SSO_REDIRECT_FLAG);
      dispatch?.({
        type: "SET_AUTH_CHECK_COMPLETE",
        payload: true
      });
      return;
    }

    // 4. Perform silent SSO check if not authenticated and not in the middle of an OAuth redirect flow
    const {
      queryString
    } = getConfigTID();
    const isAuthenticated = isUserAuthenticated(loginState);
    const hasOAuthParams = OAUTH_FLOW_PARAMS.some(param => searchParams.has(param));
    if (!isAuthenticated && !hasOAuthParams) {
      // A prior attempt redirected out and we're back on a completely clean
      // URL (no code/error/state) without authenticating — some TID/relay
      // failure paths drop the failure indicator entirely instead of coming
      // back with `error=` or `code=null` (which branch 3 above already
      // catches). Without this check, isKnownUser() would still be true and
      // this would just fire the exact same redirect again, forever.
      const ssoAttemptInFlight = sessionStorage.getItem(SSO_REDIRECT_FLAG) === "1";
      if (ssoAttemptInFlight) {
        sessionStorage.removeItem(SSO_REDIRECT_FLAG);
        dispatch?.({
          type: "SET_AUTH_CHECK_COMPLETE",
          payload: true
        });
        return;
      }

      // Skip the silent SSO redirect for visitors who have never logged in
      // (no tid_known_user cookie yet) — they can't have an active TID
      // session, so the prompt=none round-trip would just be wasted.
      if (!isKnownUser()) {
        dispatch?.({
          type: "SET_AUTH_CHECK_COMPLETE",
          payload: true
        });
        return;
      }
      const utmToPersist: Record<string, string> = {};
      for (const key_0 of UTM_PARAMS) {
        const value = searchParams.get(key_0);
        if (value) {
          utmToPersist[key_0] = value;
        }
      }
      if (Object.keys(utmToPersist).length > 0) {
        sessionStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(utmToPersist));
      } else {
        sessionStorage.removeItem(UTM_STORAGE_KEY);
      }
      const hashToPersist = window.location.hash || getLegacyTabHash(searchParams.get("tab"), window.location.pathname) || "";
      if (hashToPersist) {
        sessionStorage.setItem(HASH_STORAGE_KEY, hashToPersist);
      } else {
        sessionStorage.removeItem(HASH_STORAGE_KEY);
      }
      if (document.referrer && !document.referrer.startsWith(window.location.origin)) {
        sessionStorage.setItem(REFERRER_STORAGE_KEY, document.referrer);
      }
      sessionStorage.setItem(SSO_REDIRECT_FLAG, "1");
      const urlState = encodeURIComponent(window.location.origin + window.location.pathname);
      const url = `${queryString}${urlState}&prompt=none`;
      window.open(url, "_self");
    }
  }, [state?.isAuthCheckComplete, loginState?.token, dispatch, disableLogin]);
};
export default useAuthCheck;